import { api } from "./client";
import type { MaterialAudience } from "./portalMaterials";

// External Collection → Notifications — staff authoring API. Mirrors
// PhoenixCapital.Core.DTOs.Portal.PortalNotificationDtos and the
// /api/admin/notifications controller. Distinct from the internal staff
// notification bell (/api/notifications); this targets external accounts.

export type NotificationAudienceType = "Individual" | "Category" | "Everyone";
export type PortalNotificationType = "Announcement";

export interface PortalNotificationReadStats {
  read: number;
  total: number;
}

export interface PortalNotificationAdminDto {
  id: string;
  title: string;
  body: string;
  type: PortalNotificationType;
  audienceType: NotificationAudienceType;
  audienceSummary: string;
  categories: MaterialAudience[];
  emailSent: boolean;
  senderUserId: string | null;
  senderName: string | null;
  sentAt: string;
  readStats: PortalNotificationReadStats;
}

export interface CreatePortalNotificationPayload {
  title: string;
  body: string;
  audienceType: NotificationAudienceType;
  categories?: MaterialAudience[];
  accountIds?: string[];
  sendEmail: boolean;
}

export interface RecipientPreview {
  count: number;
}

/** One page of sent history plus the exact size of the whole set. Mirrors
 *  PortalAccountListResponse — same Items/Total shape, same Pagination footer. */
export interface PortalNotificationListResponse {
  items: PortalNotificationAdminDto[];
  total: number;
}

export const adminNotificationsApi = {
  create: (body: CreatePortalNotificationPayload) =>
    api.post<PortalNotificationAdminDto>("/admin/notifications", body),
  list: (params: { offset: number; limit: number }) =>
    api.get<PortalNotificationListResponse>("/admin/notifications", {
      offset: params.offset,
      limit: params.limit,
    }),
  get: (id: string) =>
    api.get<PortalNotificationAdminDto>(`/admin/notifications/${id}`),
  recipientPreview: (params: {
    audienceType: NotificationAudienceType;
    categories?: MaterialAudience[];
    accountIds?: string[];
  }) =>
    api.get<RecipientPreview>("/admin/notifications/recipient-preview", {
      audienceType: params.audienceType,
      categories: params.categories?.length
        ? params.categories.join(",")
        : undefined,
      accountIds: params.accountIds?.length
        ? params.accountIds.join(",")
        : undefined,
    }),
};
